import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type RowSelectionState,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { enUS, tr } from 'date-fns/locale';
import {
  ChevronLeft,
  ChevronRight,
  Filter,
  Plus,
  Search,
  Trash2,
  Pencil,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

import { listRecords, bulkDeleteRecords, deleteRecord } from '@/api/records';
import { listCategories } from '@/api/categories';
import type { Category, HealthRecord, ListRecordsParams, PageMeta } from '@/api/types';
import { Spinner } from '@/components/feedback/Spinner';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

function bmiCategory(bmi: number | null): 'underweight' | 'normal' | 'overweight' | 'obese' | null {
  if (bmi == null) return null;
  if (bmi < 18.5) return 'underweight';
  if (bmi < 25) return 'normal';
  if (bmi < 30) return 'overweight';
  return 'obese';
}

function bmiClass(bmi: number | null): string {
  const cat = bmiCategory(bmi);
  switch (cat) {
    case 'underweight':
      return 'text-sky-600 dark:text-sky-400';
    case 'normal':
      return 'text-emerald-600 dark:text-emerald-400';
    case 'overweight':
      return 'text-amber-600 dark:text-amber-400';
    case 'obese':
      return 'text-rose-600 dark:text-rose-400';
    default:
      return 'text-slate-500';
  }
}

export function RecordsListPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const dateLocale = i18n.language?.startsWith('tr') ? tr : enUS;

  // Filter state
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [sort, setSort] = useState<'date_desc' | 'date_asc'>('date_desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const params: ListRecordsParams = {
    page,
    pageSize,
    sort,
    q: q || undefined,
    from: from || undefined,
    to: to || undefined,
    categoryId: categoryId === '' ? undefined : categoryId,
  };

  const recordsQuery = useQuery({
    queryKey: ['records', params],
    queryFn: () => listRecords(params),
    placeholderData: keepPreviousData,
  });

  const categoriesQuery = useQuery({
    queryKey: ['categories'],
    queryFn: listCategories,
  });

  const categoriesById = useMemo(() => {
    const map = new Map<number, Category>();
    (categoriesQuery.data ?? []).forEach((c) => map.set(c.id, c));
    return map;
  }, [categoriesQuery.data]);

  const bulkDeleteMutation = useMutation({
    mutationFn: bulkDeleteRecords,
    onSuccess: (deleted) => {
      toast.success(t('records.deleteSuccessMulti', { count: deleted }));
      qc.invalidateQueries({ queryKey: ['records'] });
      setRowSelection({});
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const singleDeleteMutation = useMutation({
    mutationFn: deleteRecord,
    onSuccess: () => {
      toast.success(t('records.deleteSuccess'));
      qc.invalidateQueries({ queryKey: ['records'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const data = recordsQuery.data?.data ?? [];
  const meta: PageMeta | undefined = recordsQuery.data?.meta;

  const columns = useMemo<ColumnDef<HealthRecord>[]>(() => {
    return [
      {
        id: 'select',
        header: ({ table }) => (
          <input
            type="checkbox"
            aria-label="Select all rows"
            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            checked={table.getIsAllRowsSelected()}
            ref={(el) => {
              if (el) el.indeterminate = table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected();
            }}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            aria-label={`Select row ${row.original.id}`}
            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
        size: 36,
        enableSorting: false,
      },
      {
        accessorKey: 'recordDate',
        header: t('records.columns.date'),
        cell: ({ getValue }) => {
          const v = getValue<string>();
          try {
            return <span>{format(new Date(v), 'PP', { locale: dateLocale })}</span>;
          } catch {
            return <span>{v}</span>;
          }
        },
      },
      {
        id: 'category',
        header: t('records.columns.category'),
        cell: ({ row }) => {
          const id = row.original.categoryId;
          if (id == null) return <span className="text-slate-400">—</span>;
          const cat = categoriesById.get(id);
          if (!cat) return <span className="text-slate-400">#{id}</span>;
          return (
            <span className="inline-flex items-center gap-1.5">
              {cat.color && (
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: cat.color }}
                  aria-hidden
                />
              )}
              <span>{cat.name}</span>
            </span>
          );
        },
      },
      {
        accessorKey: 'heightCm',
        header: t('records.columns.height'),
        cell: ({ getValue }) => (getValue<string | null>() ?? '—'),
      },
      {
        accessorKey: 'weightKg',
        header: t('records.columns.weight'),
        cell: ({ getValue }) => (getValue<string | null>() ?? '—'),
      },
      {
        accessorKey: 'bmi',
        header: t('records.columns.bmi'),
        cell: ({ getValue }) => {
          const v = getValue<string | null>();
          if (v == null) return <span className="text-slate-400">—</span>;
          return <span className={`font-medium tabular-nums ${bmiClass(Number(v))}`}>{Number(v).toFixed(1)}</span>;
        },
      },
      {
        accessorKey: 'notes',
        header: t('records.columns.notes'),
        cell: ({ getValue }) => {
          const v = getValue<string | null>();
          if (!v) return <span className="text-slate-400">—</span>;
          return (
            <span className="block max-w-xs truncate text-slate-600 dark:text-slate-400" title={v}>
              {v}
            </span>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex items-center gap-1 justify-end">
            <Link
              to={`/records/${row.original.id}`}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100"
              aria-label={t('common.edit')}
            >
              <Pencil className="h-4 w-4" />
            </Link>
            <button
              onClick={() => {
                if (window.confirm(t('records.bulk.confirmDeleteOne'))) {
                  singleDeleteMutation.mutate(row.original.id);
                }
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30"
              aria-label={t('common.delete')}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ),
        size: 96,
      },
    ];
  }, [t, categoriesById, dateLocale, singleDeleteMutation]);

  const table = useReactTable({
    data,
    columns,
    state: { rowSelection },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.id),
    manualPagination: true,
    manualSorting: true,
  });

  const selectedCount = Object.keys(rowSelection).length;
  const hasFilters = q || from || to || categoryId !== '';

  function clearFilters() {
    setQ('');
    setFrom('');
    setTo('');
    setCategoryId('');
    setSort('date_desc');
    setPage(1);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{t('records.title')}</h1>
        <button onClick={() => navigate('/records/new')} className="btn-primary">
          <Plus className="h-4 w-4" />
          {t('records.addNew')}
        </button>
      </div>

      {/* Filters */}
      <div className="card p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Filter className="h-4 w-4" aria-hidden />
          <span>{t('records.filters.sort')}</span>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as 'date_desc' | 'date_asc')}
            className="input py-1 w-auto"
          >
            <option value="date_desc">{t('records.filters.sortDesc')}</option>
            <option value="date_asc">{t('records.filters.sortAsc')}</option>
          </select>
          {hasFilters && (
            <button onClick={clearFilters} className="ml-auto btn-secondary text-xs">
              <X className="h-3 w-3" />
              {t('records.filters.clear')}
            </button>
          )}
        </div>
        <div className="grid gap-3 grid-cols-1 md:grid-cols-4">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden />
            <input
              type="search"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              placeholder={t('records.filters.search')}
              className="input pl-9"
            />
          </div>
          <input
            type="date"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setPage(1);
            }}
            aria-label={t('records.filters.from')}
            className="input"
          />
          <input
            type="date"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              setPage(1);
            }}
            aria-label={t('records.filters.to')}
            className="input"
          />
          <select
            value={categoryId}
            onChange={(e) => {
              setCategoryId(e.target.value === '' ? '' : Number(e.target.value));
              setPage(1);
            }}
            className="input"
            aria-label={t('records.filters.category')}
          >
            <option value="">{t('records.filters.allCategories')}</option>
            {(categoriesQuery.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedCount > 0 && (
        <div className="card p-3 flex items-center justify-between bg-brand-50 dark:bg-brand-950/30 border-brand-200 dark:border-brand-900">
          <span className="text-sm font-medium text-brand-800 dark:text-brand-200">
            {t('records.bulk.selectedCount', { count: selectedCount })}
          </span>
          <button
            onClick={() => {
              if (window.confirm(t('records.bulk.confirmDelete'))) {
                const ids = Object.keys(rowSelection).map((k) => Number(k));
                bulkDeleteMutation.mutate(ids);
              }
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-rose-600 text-white px-3 py-1.5 text-sm font-medium hover:bg-rose-700"
          >
            <Trash2 className="h-4 w-4" />
            {t('records.bulk.deleteSelected')}
          </button>
        </div>
      )}

      {/* Table */}
      <div className="card overflow-hidden">
        {recordsQuery.isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner size="md" />
            <span className="ml-3 text-sm text-slate-500">{t('records.loading')}</span>
          </div>
        ) : data.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-500">
            {t('records.empty')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400">
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {hg.headers.map((h) => (
                      <th key={h.id} className="px-3 py-2 text-left font-medium" style={{ width: h.getSize() }}>
                        {flexRender(h.column.columnDef.header, h.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className="hover:bg-slate-50 dark:hover:bg-slate-900/40 transition"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-3 py-2 align-middle">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {meta && data.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
          <div className="flex items-center gap-3 text-slate-600 dark:text-slate-400">
            <span>
              {t('records.page', { page: meta.page, total: meta.totalPages })}
            </span>
            <span className="text-slate-400">·</span>
            <label className="flex items-center gap-1.5">
              <span>{t('records.pageSize')}</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="input py-1 w-auto"
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn-secondary px-2 py-1"
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
              disabled={page >= meta.totalPages}
              className="btn-secondary px-2 py-1"
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
